/**
 * Tool group configuration shared by every viewport in the app.
 *
 * Scroll, window/level, pan, zoom, measurement, freehand-segmentation,
 * bump-refinement, region-grow, measure-click.
 */

import { getCornerstone, getCornerstoneTools } from "./dicomLoader";
import { logger } from "@/utils/logger";
import { PLANE_COLORS_HEX } from "../constants/planeColors";
import {
  REFERENCE_LINES_AXIAL,
  REFERENCE_LINES_CORONAL,
  REFERENCE_LINES_SAGITTAL,
} from "../tools/planeReferenceLineTools";
import { ACTIVE_SEGMENT_COLOR, setSegmentColor } from "./segmentationConstants";
import type { SegmentationAssistConfig } from "../hooks/useLabelingQueries";

/**
 * Derive the contour-segmentation id from the labelmap segmentation id.
 *
 * Each viewport has a Labelmap segmentation (created in VolumeViewport for the
 * voxel mask). The freehand/bump/region-grow tools need a *separate* Contour
 * segmentation because cornerstone keys "active contour rep" on a segmentation
 * whose representationData includes a Contour entry — and the labelmap one
 * doesn't. Using a deterministic suffix keeps the two ids paired so the
 * SEGMENTATION_MODIFIED listener in VolumeViewport can match either.
 */
export function getContourSegmentationId(labelmapSegmentationId: string): string {
  return `${labelmapSegmentationId}__contour`;
}

/**
 * Ensure a Contour-type segmentation paired with the given labelmap exists,
 * is registered as a Contour rep on every viewport, and is the active
 * segmentation. PlanarFreehandContourSegmentationTool, BumpContourTool, and
 * RegionGrowTool all require this — without it the first stroke throws
 * "A contour segmentation must be active" and silently drops the input.
 *
 * Idempotent.
 */
export function ensureContourSegmentationActive(
  labelmapSegmentationId: string,
  segmentIndex: number = 1,
): void {
  if (!labelmapSegmentationId) return;
  const cornerstone = getCornerstone();
  const { segmentation, Enums: ToolEnums } = getCornerstoneTools();
  const contourSegId = getContourSegmentationId(labelmapSegmentationId);

  // Register the contour-type segmentation if missing.
  const allSegs: any[] = segmentation.state.getSegmentations?.() ?? [];
  const segExists = allSegs.some((s: any) => s.segmentationId === contourSegId);
  if (!segExists) {
    try {
      segmentation.addSegmentations([
        {
          segmentationId: contourSegId,
          representation: { type: ToolEnums.SegmentationRepresentations.Contour },
        },
      ]);
    } catch (e) {
      logger.warn("[ensureContourSegmentationActive] addSegmentations failed:", e);
    }
  }

  const engines = cornerstone.getRenderingEngines?.() ?? [];
  for (const engine of engines) {
    for (const vp of engine.getViewports?.() ?? []) {
      const vpId: string = vp.id;
      const reps = segmentation.state.getSegmentationRepresentations?.(vpId) ?? [];
      const hasContour = reps.some(
        (r: any) =>
          r.segmentationId === contourSegId &&
          r.type === ToolEnums.SegmentationRepresentations.Contour,
      );
      if (!hasContour) {
        try {
          segmentation.addSegmentationRepresentations(vpId, [
            {
              segmentationId: contourSegId,
              type: ToolEnums.SegmentationRepresentations.Contour,
            },
          ]);
        } catch (e) {
          logger.warn(
            `[ensureContourSegmentationActive] addSegmentationRepresentations failed for ${vpId}:`,
            e,
          );
          continue;
        }
      }
      try {
        segmentation.activeSegmentation.setActiveSegmentation(vpId, contourSegId);
      } catch (e) {
        logger.debug(
          `[ensureContourSegmentationActive] setActiveSegmentation skipped for ${vpId}:`,
          e,
        );
      }
      try {
        setSegmentColor(
          segmentation.config,
          vpId,
          contourSegId,
          segmentIndex,
          ACTIVE_SEGMENT_COLOR,
        );
      } catch (e) {
        logger.debug(`[ensureContourSegmentationActive] setSegmentColor skipped for ${vpId}:`, e);
      }
    }
  }

  // Tell Cornerstone which segment index the contour tools should attach
  // new annotations to. Without this, the lasso (PlanarFreehandContour-
  // SegmentationTool) defaults to segment 1 forever, so the 2nd stone in a
  // multi-stone case is stamped with the same index as the 1st — the save
  // filter (`segmentIndex === activeSegmentIndex`) then finds zero matches
  // and the Save button stays disabled.
  try {
    segmentation.segmentIndex?.setActiveSegmentIndex?.(contourSegId, segmentIndex);
  } catch (e) {
    logger.debug("[ensureContourSegmentationActive] setActiveSegmentIndex skipped:", e);
  }
}

/**
 * Read-only toolgroup for sibling viewports (MIP, reference-only views) that
 * share the same volume as an annotating viewport. Wires navigation +
 * windowing only; intentionally omits PlanarFreehandContourSegmentationTool,
 * BumpContourTool, RegionGrowTool, MeasureClickTool, Length, RectangleROI,
 * and reference lines so the user can't draw on a viewport whose
 * segmentation state isn't attached.
 */
export function configureReadonlyToolGroup(toolGroup: any): void {
  const { Enums: ToolEnums } = getCornerstoneTools();

  toolGroup.addTool("StackScroll", { invert: true });
  toolGroup.addTool("WindowLevel");
  toolGroup.addTool("Pan");
  toolGroup.addTool("Zoom");

  toolGroup.setToolActive("StackScroll", {
    bindings: [{ mouseButton: ToolEnums.MouseBindings.Wheel }],
  });
  toolGroup.setToolActive("WindowLevel", {
    bindings: [{ mouseButton: ToolEnums.MouseBindings.Primary }],
  });
  toolGroup.setToolActive("Pan", {
    bindings: [{ mouseButton: ToolEnums.MouseBindings.Auxiliary }],
  });
  toolGroup.setToolActive("Zoom", {
    bindings: [{ mouseButton: ToolEnums.MouseBindings.Secondary }],
  });
}

/** Register and configure tools on a newly-created tool group. */
export function configureToolGroup(toolGroup: any): void {
  const { Enums: ToolEnums } = getCornerstoneTools();

  // Navigation + windowing
  // AnatomicScroll routes wheel + middle-drag through the slot's
  // IPP-derived `listAnatomicSign` so wheel-up always moves anatomically up
  // (cranial / anterior / patient-right), independent of imageIds sort order.
  toolGroup.addTool("AnatomicScroll");
  toolGroup.addTool("WindowLevel");
  toolGroup.addTool("Pan");
  toolGroup.addTool("Zoom");

  // Measurement tools (passive — activated via keyboard shortcuts)
  toolGroup.addTool("Length");
  toolGroup.addTool("RectangleROI");

  // Mouse bindings
  toolGroup.setToolActive("AnatomicScroll", {
    bindings: [{ mouseButton: ToolEnums.MouseBindings.Wheel }],
  });
  toolGroup.setToolActive("WindowLevel", {
    bindings: [{ mouseButton: ToolEnums.MouseBindings.Primary }],
  });
  toolGroup.setToolActive("Pan", {
    bindings: [{ mouseButton: ToolEnums.MouseBindings.Auxiliary }],
  });
  toolGroup.setToolActive("Zoom", {
    bindings: [{ mouseButton: ToolEnums.MouseBindings.Secondary }],
  });

  toolGroup.setToolPassive("Length");
  toolGroup.setToolPassive("RectangleROI");

  // Segmentation: freehand contour. Interpolation off — with it on,
  // Cornerstone's InterpolationManager spawns dashed auto-generated contours
  // on neighboring slices on every ANNOTATION_COMPLETED, producing the
  // "dashed lines radiating to center" artifact in the volume viewport.
  toolGroup.addTool("PlanarFreehandContourSegmentationTool", {
    interpolation: { enabled: false },
  });
  toolGroup.setToolPassive("PlanarFreehandContourSegmentationTool");

  // Bump contour refinement — sculpting brush for freehand contours
  toolGroup.addTool("BumpContourTool");
  toolGroup.setToolPassive("BumpContourTool");

  // Region grow — one-click flood-fill segmentation
  toolGroup.addTool("RegionGrowTool");
  toolGroup.setToolPassive("RegionGrowTool");

  // Click-to-measure (📐 case-viewer flow). Activated transiently when
  // measureMode is set; passive otherwise.
  toolGroup.addTool("MeasureClickTool");
  toolGroup.setToolPassive("MeasureClickTool");

  // Reference lines: 3 plane-specific tools, registered disabled. Each carries
  // the source plane's color. useReferenceLinesSync sets the sourceViewportId
  // per tool (by classifying viewports' viewPlaneNormal) and toggles enabled
  // state based on the atom.
  // showFullDimension=true so lines stay anchored to the viewport edges
  // during zoom/pan; the subclasses override getTargetId so the early-load
  // throw is neutralized (see planeReferenceLineTools.ts).
  const REF_LINES_CONFIG = { sourceViewportId: undefined, showFullDimension: true };
  toolGroup.addTool(REFERENCE_LINES_AXIAL, REF_LINES_CONFIG);
  toolGroup.addTool(REFERENCE_LINES_CORONAL, REF_LINES_CONFIG);
  toolGroup.addTool(REFERENCE_LINES_SAGITTAL, REF_LINES_CONFIG);
  toolGroup.setToolDisabled(REFERENCE_LINES_AXIAL);
  toolGroup.setToolDisabled(REFERENCE_LINES_CORONAL);
  toolGroup.setToolDisabled(REFERENCE_LINES_SAGITTAL);

  // Color each plane's reference lines to match the 3D segmentation viewer's
  // legend (axial=blue, coronal=green, sagittal=red — see PLANE_COLORS_HEX).
  // ReferenceLines' auto-generated annotation has highlighted=true, so the
  // style hierarchy resolves colorHighlighted before color — both must be set
  // or the default `colorHighlighted: rgb(0,255,0)` wins and every line is
  // bright green.
  const csToolsForStyle = getCornerstoneTools();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const style = (csToolsForStyle as any).annotation?.config?.style;
  if (style?.setToolGroupToolStyles) {
    const REF_LINE_WIDTH = "1";
    const planeStyles = (hex: string) => ({
      color: hex,
      colorHighlighted: hex,
      lineWidth: REF_LINE_WIDTH,
      lineWidthHighlighted: REF_LINE_WIDTH,
    });
    style.setToolGroupToolStyles(toolGroup.id, {
      [REFERENCE_LINES_AXIAL]: planeStyles(PLANE_COLORS_HEX.axial),
      [REFERENCE_LINES_CORONAL]: planeStyles(PLANE_COLORS_HEX.coronal),
      [REFERENCE_LINES_SAGITTAL]: planeStyles(PLANE_COLORS_HEX.sagittal),
    });
  } else {
    logger.warn(
      "[configureToolGroup] cornerstone-tools annotation.config.style API not found; reference-line colors will fall back to default.",
    );
  }
}

/** All tools that can be bound to the primary mouse button. */
const ALL_PRIMARY_TOOLS = [
  "WindowLevel",
  "Pan",
  "Zoom",
  "Length",
  "RectangleROI",
  "PlanarFreehandContourSegmentationTool",
  "BumpContourTool",
  "RegionGrowTool",
  "MeasureClickTool",
];

/** Map from activeMeasurementTool atom value → Cornerstone tool name. */
const TOOL_NAME_MAP: Record<string, string> = {
  pan: "Pan",
  zoom: "Zoom",
  length: "Length",
  rectangleROI: "RectangleROI",
  lasso: "PlanarFreehandContourSegmentationTool",
  bump: "BumpContourTool",
  regionGrow: "RegionGrowTool",
  measure: "MeasureClickTool",
};

/**
 * Tools that bind safely on a Cornerstone STACK viewport — i.e., don't
 * depend on a labelmap volume or a Contour-typed segmentation rep, both of
 * which only VolumeViewport sets up. StackViewport's tool-binding effect
 * uses this set to refuse contour-segmentation modes (lasso/bump/regionGrow)
 * and the volume-only MeasureClickTool flow.
 *
 * "measure" is intentionally absent: `MeasurementToolMode` doesn't include
 * it — it's a transient synthesized value `effectiveTool = "measure"` from
 * VolumeViewport (see VolumeViewport.tsx around the measureMode block) and
 * never reaches the activeMeasurementToolAtom we're gating against.
 *
 * Co-located with TOOL_NAME_MAP so a future tool addition (ellipseROI,
 * angle, etc.) is forced to declare its stack-safety at the same site
 * rather than drifting across files.
 */
export const STACK_SAFE_TOOLS: ReadonlySet<string> = new Set([
  "none",
  "pan",
  "zoom",
  "length",
  "rectangleROI",
]);

/**
 * Switch which tool is active on the primary mouse button.
 * All other primary tools are set to passive first.
 */
export function switchActiveTool(
  toolGroup: any,
  activeTool: string,
  options?: { measureRefinement?: boolean },
): void {
  const { Enums: ToolEnums } = getCornerstoneTools();

  // Set all primary tools to passive (ignore errors for tools not in this group)
  for (const name of ALL_PRIMARY_TOOLS) {
    try {
      toolGroup.setToolPassive(name);
    } catch {
      /* tool not registered */
    }
  }

  const toolName = TOOL_NAME_MAP[activeTool] ?? "WindowLevel";
  // Cornerstone's mouse dispatcher requires `binding.modifierKey ===
  // modifierKey` to match — plain primary, shift+primary, and
  // ctrl/meta+primary are three SEPARATE bindings. Without explicit
  // shift/ctrl bindings, modifier-clicks fall through to no tool. The
  // measure tool needs all three so refinement (shift = positive,
  // ctrl/meta = negative) reaches its preMouseDownCallback.
  const bindings: Array<{ mouseButton: number; modifierKey?: number }> = [
    { mouseButton: ToolEnums.MouseBindings.Primary },
  ];
  if (toolName === "MeasureClickTool") {
    bindings.push(
      {
        mouseButton: ToolEnums.MouseBindings.Primary,
        modifierKey: ToolEnums.KeyboardBindings.Shift,
      },
      {
        mouseButton: ToolEnums.MouseBindings.Primary,
        modifierKey: ToolEnums.KeyboardBindings.Ctrl,
      },
      {
        mouseButton: ToolEnums.MouseBindings.Primary,
        modifierKey: ToolEnums.KeyboardBindings.Meta,
      },
    );
  }
  toolGroup.setToolActive(toolName, { bindings });

  // Refinement-only mode: the primary tool above stays bound to plain
  // primary (W/L, pan, etc.), and MeasureClickTool listens *only* on
  // shift/ctrl/meta + primary so unmodified clicks fall through to the
  // user's chosen tool. Used after the first measure click resolves.
  if (options?.measureRefinement && toolName !== "MeasureClickTool") {
    try {
      toolGroup.setToolActive("MeasureClickTool", {
        bindings: [
          {
            mouseButton: ToolEnums.MouseBindings.Primary,
            modifierKey: ToolEnums.KeyboardBindings.Shift,
          },
          {
            mouseButton: ToolEnums.MouseBindings.Primary,
            modifierKey: ToolEnums.KeyboardBindings.Ctrl,
          },
          {
            mouseButton: ToolEnums.MouseBindings.Primary,
            modifierKey: ToolEnums.KeyboardBindings.Meta,
          },
        ],
      });
    } catch {
      /* MeasureClickTool not registered in this group */
    }
  }
}

/**
 * Check whether a V2 segmentation tool is available for the current lesion type.
 *
 * Returns false if config is null (not loaded) — no fallback, tools stay
 * unavailable until the config is fetched from the backend.
 *
 * @param config  Current lesion type's segmentation assist config (from atom)
 * @param tool    Which V2 tool to check
 */
export function isV2ToolEnabled(
  config: SegmentationAssistConfig | null,
  tool: "propagation" | "edge_snap" | "region_grow",
): boolean {
  if (!config) return false;
  return config[tool].enabled;
}
